import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  DataUsage as DataUsageIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  Public as PublicIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { getFirebaseUsage, FirebaseUsageStats } from '../../services/firebaseUsageService';
import UsageStatCard from '../../components/firebase/UsageStatCard';
import UsageChart from '../../components/firebase/UsageChart';
import UsageDetailsTable from '../../components/firebase/UsageDetailsTable';

// واجهة خصائص TabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// مكون TabPanel
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`usage-tabpanel-${index}`}
      aria-labelledby={`usage-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// تنسيق التاريخ للعرض
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('ar-EG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// تنسيق حجم البيانات للعرض
const formatSize = (bytes: number): string => {
  if (bytes === 0) return '0 بايت';

  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت', 'تيرابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// صفحة حساب استخدام فايربيز
const FirebaseUsagePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<FirebaseUsageStats | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // جلب بيانات الاستخدام عند تحميل الصفحة
  useEffect(() => {
    fetchUsageData();
  }, []);

  // جلب بيانات الاستخدام
  const fetchUsageData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getFirebaseUsage();
      setUsageData(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching Firebase usage data:', err);
      setError('حدث خطأ أثناء جلب بيانات استخدام فايربيز. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // تحديث بيانات الاستخدام
  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsageData();
  };

  // تغيير التبويب النشط
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // إذا كان جاري التحميل، عرض مؤشر التحميل
  if (loading && !refreshing) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // إذا كان هناك خطأ، عرض رسالة الخطأ
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ mt: 2 }}
        >
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  // إذا لم تكن هناك بيانات، عرض رسالة
  if (!usageData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">لا توجد بيانات استخدام متاحة.</Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ mt: 2 }}
        >
          تحديث البيانات
        </Button>
      </Box>
    );
  }

  // إعداد بيانات جدول تفاصيل التخزين
  const storageTableData = [
    {
      name: 'صور المنتجات',
      size: usageData.currentUsage.storage.used * 0.6,
      files: Math.round(usageData.currentUsage.storage.files * 0.7),
      percentage: Math.round((usageData.currentUsage.storage.used * 0.6) / usageData.limits.storage * 100)
    },
    {
      name: 'ملفات المستخدمين',
      size: usageData.currentUsage.storage.used * 0.2,
      files: Math.round(usageData.currentUsage.storage.files * 0.2),
      percentage: Math.round((usageData.currentUsage.storage.used * 0.2) / usageData.limits.storage * 100)
    },
    {
      name: 'ملفات الصيانة',
      size: usageData.currentUsage.storage.used * 0.15,
      files: Math.round(usageData.currentUsage.storage.files * 0.08),
      percentage: Math.round((usageData.currentUsage.storage.used * 0.15) / usageData.limits.storage * 100)
    },
    {
      name: 'ملفات أخرى',
      size: usageData.currentUsage.storage.used * 0.05,
      files: Math.round(usageData.currentUsage.storage.files * 0.02),
      percentage: Math.round((usageData.currentUsage.storage.used * 0.05) / usageData.limits.storage * 100)
    }
  ];

  // إعداد بيانات جدول تفاصيل قاعدة البيانات
  const databaseTableData = [
    {
      collection: 'المنتجات',
      documents: Math.round(usageData.currentUsage.database.documents * 0.3),
      reads: Math.round(usageData.currentUsage.database.reads * 0.4),
      writes: Math.round(usageData.currentUsage.database.writes * 0.25),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.2)
    },
    {
      collection: 'المستخدمين',
      documents: Math.round(usageData.currentUsage.database.documents * 0.1),
      reads: Math.round(usageData.currentUsage.database.reads * 0.2),
      writes: Math.round(usageData.currentUsage.database.writes * 0.1),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.05)
    },
    {
      collection: 'الطلبات',
      documents: Math.round(usageData.currentUsage.database.documents * 0.25),
      reads: Math.round(usageData.currentUsage.database.reads * 0.15),
      writes: Math.round(usageData.currentUsage.database.writes * 0.3),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.1)
    },
    {
      collection: 'الصيانة',
      documents: Math.round(usageData.currentUsage.database.documents * 0.2),
      reads: Math.round(usageData.currentUsage.database.reads * 0.15),
      writes: Math.round(usageData.currentUsage.database.writes * 0.25),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.4)
    },
    {
      collection: 'الإعدادات',
      documents: Math.round(usageData.currentUsage.database.documents * 0.05),
      reads: Math.round(usageData.currentUsage.database.reads * 0.05),
      writes: Math.round(usageData.currentUsage.database.writes * 0.05),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.05)
    },
    {
      collection: 'أخرى',
      documents: Math.round(usageData.currentUsage.database.documents * 0.1),
      reads: Math.round(usageData.currentUsage.database.reads * 0.05),
      writes: Math.round(usageData.currentUsage.database.writes * 0.05),
      deletes: Math.round(usageData.currentUsage.database.deletes * 0.2)
    }
  ];

  return (
    <Box>
      {/* عنوان الصفحة وزر التحديث */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          حساب استخدام فايربيز
        </Typography>
        <Box>
          {lastUpdated && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'left' }}>
              آخر تحديث: {formatDate(lastUpdated)}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'جاري التحديث...' : 'تحديث البيانات'}
          </Button>
        </Box>
      </Box>

      {/* بطاقات الإحصائيات */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="التخزين"
            value={usageData.currentUsage.storage.used}
            max={usageData.limits.storage}
            icon="storage"
            description="إجمالي مساحة التخزين المستخدمة"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="قراءات قاعدة البيانات"
            value={usageData.currentUsage.database.reads}
            max={usageData.limits.databaseReads}
            unit="قراءة"
            icon="database"
            description="عدد عمليات القراءة اليومية"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="كتابات قاعدة البيانات"
            value={usageData.currentUsage.database.writes}
            max={usageData.limits.databaseWrites}
            unit="كتابة"
            icon="database"
            description="عدد عمليات الكتابة اليومية"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="المستخدمين"
            value={usageData.currentUsage.auth.users}
            max={usageData.limits.authUsers}
            unit="مستخدم"
            icon="auth"
            description="إجمالي عدد المستخدمين المسجلين"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="استدعاءات الدوال"
            value={usageData.currentUsage.functions.invocations}
            max={usageData.limits.functionInvocations}
            unit="استدعاء"
            icon="functions"
            description="عدد استدعاءات الدوال اليومية"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <UsageStatCard
            title="تخزين Cloudinary"
            value={usageData.currentUsage.cloudinary?.storage || 0}
            max={10 * 1024 * 1024 * 1024} // 10GB
            icon="storage"
            description="مساحة تخزين الصور في Cloudinary"
          />
        </Grid>
      </Grid>

      {/* تبويبات التفاصيل */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<StorageIcon />} label="التخزين" iconPosition="start" />
          <Tab icon={<DataUsageIcon />} label="قاعدة البيانات" iconPosition="start" />
          <Tab icon={<PersonIcon />} label="المصادقة" iconPosition="start" />
          <Tab icon={<CodeIcon />} label="الدوال السحابية" iconPosition="start" />
          <Tab icon={<PublicIcon />} label="الاستضافة" iconPosition="start" />
          <Tab icon={<CloudUploadIcon />} label="Cloudinary" iconPosition="start" />
        </Tabs>

        {/* محتوى تبويب التخزين */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="استخدام التخزين (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="storage"
                subKey="used"
                yAxisLabel="الحجم"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="توزيع ملفات التخزين"
                data={storageTableData}
                type="pie"
                dataKey="size"
                height={300}
              />
            </Grid>
            <Grid item xs={12}>
              <UsageDetailsTable
                title="تفاصيل استخدام التخزين"
                data={storageTableData}
                columns={[
                  { id: 'name', label: 'النوع', align: 'right', minWidth: 170 },
                  {
                    id: 'size',
                    label: 'الحجم',
                    align: 'right',
                    format: (value) => formatSize(value)
                  },
                  {
                    id: 'files',
                    label: 'عدد الملفات',
                    align: 'right',
                    format: (value) => value.toLocaleString('ar-EG')
                  },
                  {
                    id: 'percentage',
                    label: 'نسبة الاستخدام',
                    align: 'right',
                    renderProgress: true,
                    maxValue: 100,
                    format: (value) => `${value}%`
                  }
                ]}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى تبويب قاعدة البيانات */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عمليات قاعدة البيانات (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="database"
                height={300}
                yAxisLabel="عدد العمليات"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="توزيع المستندات حسب المجموعة"
                data={databaseTableData}
                type="pie"
                dataKey="documents"
                height={300}
              />
            </Grid>
            <Grid item xs={12}>
              <UsageDetailsTable
                title="تفاصيل استخدام قاعدة البيانات"
                data={databaseTableData}
                columns={[
                  { id: 'collection', label: 'المجموعة', align: 'right', minWidth: 170 },
                  {
                    id: 'documents',
                    label: 'عدد المستندات',
                    align: 'right',
                    format: (value) => value.toLocaleString('ar-EG')
                  },
                  {
                    id: 'reads',
                    label: 'القراءات',
                    align: 'right',
                    format: (value) => value.toLocaleString('ar-EG')
                  },
                  {
                    id: 'writes',
                    label: 'الكتابات',
                    align: 'right',
                    format: (value) => value.toLocaleString('ar-EG')
                  },
                  {
                    id: 'deletes',
                    label: 'عمليات الحذف',
                    align: 'right',
                    format: (value) => value.toLocaleString('ar-EG')
                  }
                ]}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى تبويب المصادقة */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عدد المستخدمين (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="auth"
                subKey="users"
                height={300}
                yAxisLabel="عدد المستخدمين"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عمليات تسجيل الدخول (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="bar"
                dataKey="auth"
                subKey="logins"
                height={300}
                yAxisLabel="عدد عمليات تسجيل الدخول"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى تبويب الدوال السحابية */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="استدعاءات الدوال (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="functions"
                subKey="invocations"
                height={300}
                yAxisLabel="عدد الاستدعاءات"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="أخطاء الدوال (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="bar"
                dataKey="functions"
                subKey="errors"
                height={300}
                yAxisLabel="عدد الأخطاء"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى تبويب الاستضافة */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="طلبات الاستضافة (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="hosting"
                subKey="requests"
                height={300}
                yAxisLabel="عدد الطلبات"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عرض النطاق المستخدم (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="bar"
                dataKey="hosting"
                subKey="bandwidth"
                height={300}
                yAxisLabel="عرض النطاق"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* محتوى تبويب Cloudinary */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="استخدام التخزين في Cloudinary (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="cloudinary"
                subKey="storage"
                height={300}
                yAxisLabel="الحجم"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عرض النطاق المستخدم في Cloudinary (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="bar"
                dataKey="cloudinary"
                subKey="bandwidth"
                height={300}
                yAxisLabel="عرض النطاق"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عدد الصور المخزنة في Cloudinary (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="line"
                dataKey="cloudinary"
                subKey="images"
                height={300}
                yAxisLabel="عدد الصور"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <UsageChart
                title="عدد التحويلات في Cloudinary (آخر 30 يوم)"
                data={usageData.dailyUsage}
                type="bar"
                dataKey="cloudinary"
                subKey="transformations"
                height={300}
                yAxisLabel="عدد التحويلات"
              />
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InfoIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">معلومات عن Cloudinary</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" paragraph>
                  Cloudinary هي خدمة سحابية لإدارة الوسائط تتيح تخزين ومعالجة وتسليم الصور والفيديوهات. يستخدم النظام Cloudinary لتخزين صور المنتجات والفئات وغيرها من الصور المستخدمة في المتجر.
                </Typography>
                <Typography variant="body1" paragraph>
                  <strong>مميزات استخدام Cloudinary:</strong>
                </Typography>
                <ul>
                  <li>تحميل الصور بسرعة وكفاءة عالية</li>
                  <li>تحويل وتعديل الصور تلقائياً (تغيير الحجم، القص، إضافة تأثيرات)</li>
                  <li>تحسين أداء المتجر من خلال تقديم الصور بأحجام مناسبة لكل جهاز</li>
                  <li>توفير مساحة التخزين في قاعدة البيانات الرئيسية</li>
                  <li>تسريع تحميل صفحات المتجر باستخدام شبكة توزيع المحتوى (CDN)</li>
                </ul>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* توصيات لتحسين الاستخدام */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <InfoIcon color="info" sx={{ mr: 1 }} />
          <Typography variant="h6">توصيات لتحسين استخدام فايربيز</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>تحسين استخدام التخزين:</Typography>
            <ul>
              <li>ضغط الصور قبل رفعها لتقليل حجم التخزين المستخدم</li>
              <li>حذف الملفات القديمة وغير المستخدمة بشكل دوري</li>
              <li>استخدام تقنيات التخزين المؤقت للملفات المستخدمة بكثرة</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>تحسين استخدام قاعدة البيانات:</Typography>
            <ul>
              <li>تحسين استعلامات قاعدة البيانات لتقليل عدد القراءات</li>
              <li>استخدام التخزين المؤقت للبيانات المستخدمة بكثرة</li>
              <li>تجنب جلب بيانات غير ضرورية في الاستعلامات</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>تحسين استخدام المصادقة:</Typography>
            <ul>
              <li>إزالة الحسابات غير النشطة بشكل دوري</li>
              <li>استخدام آليات تسجيل الدخول المستمر لتقليل عدد عمليات تسجيل الدخول</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>تحسين استخدام الدوال السحابية:</Typography>
            <ul>
              <li>تحسين أداء الدوال لتقليل وقت التنفيذ وتكلفة الاستخدام</li>
              <li>تجميع العمليات المتشابهة في دالة واحدة لتقليل عدد الاستدعاءات</li>
              <li>استخدام آليات التخزين المؤقت للنتائج المتكررة</li>
            </ul>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>تحسين استخدام Cloudinary:</Typography>
            <ul>
              <li>ضغط الصور قبل رفعها إلى Cloudinary لتقليل مساحة التخزين المستخدمة</li>
              <li>استخدام التحويلات المناسبة للصور حسب حجم الشاشة</li>
              <li>حذف الصور القديمة وغير المستخدمة بشكل دوري</li>
              <li>استخدام التخزين المؤقت للصور المستخدمة بكثرة</li>
            </ul>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default FirebaseUsagePage;
